import type { Schemas } from '@argzombie/api-client';

/*
 * The Stories & Tech Step 3 content types (lure run #128, sniper #129), from the generated client (S3-INT regenerated it from the integrated
 * API, so the streams' additive fields are in it and the local stand-ins are gone). Kept as one module so the forms import short names.
 */

export type VehicleDef = Schemas['VehicleDef'];
export type VehicleSeatDef = Schemas['VehicleSeatDef'];
export type VehicleNoiseDef = Schemas['VehicleNoiseDef'];
export type VehicleFuelDef = Schemas['VehicleFuelDef'];
export type VehicleRamDef = Schemas['VehicleRamDef'];
export type VehicleClingDef = Schemas['VehicleClingDef'];
/** `baseItemId` is the item weapon the mount starts from (default `lmg`); `noiseM` 0 = the base item's. */
export type VehicleMountDef = Schemas['VehicleMountDef'];
export type WeaponDef = Schemas['WeaponDef'];
export type ScopeDef = Schemas['ScopeDef'];

export type ArenaTemplateDef = Schemas['ArenaTemplateDef'];
export type ArenaPointDef = Schemas['ArenaPointDef'];
export type ArenaRectDef = Schemas['ArenaRectDef'];
export type ArenaSpawnDef = Schemas['ArenaSpawnDef'];
export type ArenaObjectiveDef = Schemas['ArenaObjectiveDef'];
export type ArenaRulesDef = Schemas['ArenaRulesDef'];
export type ArenaGrabDef = Schemas['ArenaGrabDef'];
export type ArenaCrowdDef = Schemas['ArenaCrowdDef'];
export type LureSiteRulesDef = Schemas['LureSiteRulesDef'];
/** S3-ARS: a scenario of kind `arena`'s block (`scenarios[].arena`, shared-cs `ScenarioArenaDef`). */
export type ScenarioArenaDef = Schemas['ScenarioArenaDef'];

export type OverwatchDef = Schemas['OverwatchDef'];
export type OverwatchRulesDef = Schemas['OverwatchRulesDef'];
export type OverwatchSceneDef = Schemas['OverwatchSceneDef'];
export type OverwatchPerchDef = Schemas['OverwatchPerchDef'];
export type OverwatchPlanDef = Schemas['OverwatchPlanDef'];
export type OverwatchZoneDef = Schemas['OverwatchZoneDef'];
export type OverwatchPointDef = Schemas['OverwatchPointDef'];
export type OverwatchBoxDef = Schemas['OverwatchBoxDef'];
export type OverwatchFigureDef = Schemas['OverwatchFigureDef'];
export type OverwatchMissionDef = Schemas['OverwatchMissionDef'];
export type OverwatchPhaseDef = Schemas['OverwatchPhaseDef'];
export type OverwatchObjectiveDef = Schemas['OverwatchObjectiveDef'];
export type OverwatchSpawnDef = Schemas['OverwatchSpawnDef'];
export type OverwatchWavesDef = Schemas['OverwatchWavesDef'];
export type OverwatchShot = Schemas['OverwatchShot'];

/** shared-cs `VehicleSeatRoles.All`. */
export const SEAT_ROLES = ['driver', 'pillion', 'window', 'cab', 'bed', 'mount'] as const;
/** shared-cs `ArenaObjectiveKinds.All`. */
export const ARENA_OBJECTIVE_KINDS = ['supply_truck', 'survivor'] as const;
/** The edges a reinforcement wave can come from (`ArenaTemplateDef.ReinforceFrom`). */
export const ARENA_EDGES = ['north', 'east', 'south', 'west'] as const;
/** The building categories a lure site or a template fits (lurerun.html). */
export const LURE_CATEGORIES = ['food', 'supply_cache', 'hardware', 'medical'] as const;
/** The area density tiers (`LureSiteRulesDef.HordeByTier`, ADR 0021). */
export const DENSITY_TIERS = ['dense', 'normal', 'sparse', 'empty'] as const;
/** Footprint classes (`LureSiteRulesDef.MinFootprint`). */
export const FOOTPRINTS = ['small', 'medium', 'large'] as const;

/** shared-cs `OverwatchTraitAxes.All`. */
export const TRAIT_AXES = ['jacket', 'feature', 'carry', 'bandage'] as const;
/** shared-cs `OverwatchRoles.All`. */
export const OW_ROLES = ['innocent', 'escort', 'hostile'] as const;
/** shared-cs `OverwatchRoutineKinds.All`. */
export const ROUTINE_KINDS = [
  'patrol',
  'wander',
  'scripted',
  'flee',
  'converge',
  'chase',
  'follow',
] as const;
/** shared-cs `OverwatchObjectiveTypes.All`. */
export const OW_OBJECTIVE_TYPES = ['kill_target', 'escort', 'no_kill'] as const;
/** shared-cs `OverwatchNoKill`. */
export const NO_KILL_MODES = ['soft', 'hard'] as const;
/** Figure kinds (`OverwatchFigureDef.Kind`). */
export const FIGURE_KINDS = ['human', 'zombie'] as const;
/** shared-cs `OverwatchEndings`. */
export const OW_ENDINGS = ['complete', 'extracted', 'one_more_shot', 'failed'] as const;
